import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { StudyDetailResponse } from '@/api/generated/types.gen';
import {
  handleUpdateStudyStudiesStudyIdPatchMutation,
  handleGetStudyStudiesStudyIdGetQueryKey,
  handleListStudiesStudiesGetQueryKey,
} from '@/api/generated/@tanstack/react-query.gen';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import { SimpleTooltip } from '@/components/SimpleTooltip';
import { useStore } from '@/stores/useStore';
import { cn } from '@/lib/utils';
import {
  GCI_SWEEP_AVAILABLE,
  SURROGATE_MODEL_AVAILABLE,
} from '../-data/feature-flags';
import { Spinner } from '@/components/ui/spinner';
import { Badge, badgeVariants } from '@/components/ui/badge';
import { RowLabel } from '@/components/RowLabel';

type Kind = 'doe' | 'surrogate' | 'gci';

interface KindOption {
  kind: Kind;
  disabled?: boolean;
}

export const OPTIONS: KindOption[] = [
  { kind: 'doe' },
  {
    kind: 'surrogate',
    disabled: !SURROGATE_MODEL_AVAILABLE,
  },
  {
    kind: 'gci',
    disabled: !GCI_SWEEP_AVAILABLE,
  },
];

/**
 * What the study-kind pill adds ON TOP of `<Badge variant="muted" size="xs">`.
 *
 * The primitive already supplies the whole pill shape — `inline-flex`,
 * `rounded-full`, `px-2 py-0.5`, `gap-1`, the `bg-muted`/`text-muted-foreground`
 * neutral tint and `text-3xs`. Left here is only what is specific to this chip:
 * the mono small-caps face, and the visible border, because every tinted
 * variant in `badge.tsx` ships `border-transparent`.
 */
const PILL_CLASS =
  'border-border font-mono font-semibold tracking-caps uppercase';

// The picker's own option labels, so the badge and the menu cannot disagree
// and neither renders English on another locale (ENG-3097).
const LABEL_KEY = {
  doe: 'simopsShared.studyKind.options.doe.label',
  surrogate: 'simopsShared.studyKind.options.surrogate.label',
  gci: 'simopsShared.studyKind.options.gci.label',
} as const;

interface StudyKindBadgeProps {
  study: StudyDetailResponse;
}

export default function StudyKindBadge({ study }: StudyKindBadgeProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const setStudyKind = useStore((s) => s.setStudyKind);
  const [open, setOpen] = useState(false);
  const current = (study.config?.kind ?? 'doe') as Kind;
  const labelKey = LABEL_KEY[current as keyof typeof LABEL_KEY];
  const label = labelKey ? t(labelKey) : current.toUpperCase();
  const isDraft = study.status === 'draft';

  const updateStudy = useMutation({
    ...handleUpdateStudyStudiesStudyIdPatchMutation(),
    onSuccess: (_data, variables) => {
      // Mirror the persisted kind into the draft store so DraftSetup's
      // autosave doesn't full-replace `config` without `kind` and silently
      // reset the discriminator on the next keystroke.
      const next = variables.body?.config?.kind as Kind | undefined;
      if (next) setStudyKind(next);
      void queryClient.invalidateQueries({
        queryKey: handleGetStudyStudiesStudyIdGetQueryKey({
          path: { study_id: study.id },
        }),
      });
      void queryClient.invalidateQueries({
        queryKey: handleListStudiesStudiesGetQueryKey(),
      });
      setOpen(false);
    },
  });

  const handlePick = (next: Kind) => {
    if (next === current) {
      setOpen(false);
      return;
    }
    updateStudy.mutate({
      path: { study_id: study.id },
      body: {
        config: {
          ...(study.config ?? {}),
          kind: next,
        },
      },
    });
  };

  if (!isDraft) {
    return (
      <SimpleTooltip content={t('simopsShared.studyKind.fixedTooltip')}>
        <Badge variant="muted" size="xs" className={PILL_CLASS}>
          {label}
        </Badge>
      </SimpleTooltip>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <SimpleTooltip content={t('simopsShared.studyKind.changeTooltip')}>
        <PopoverTrigger asChild>
          <button
            type="button"
            // `badgeVariants(...)` rather than `<Badge asChild>`: Slot
            // CONCATENATES the two className strings instead of merging them,
            // so the badge's `focus-visible:ring-[3px] focus-visible:ring-ring/50`
            // and this button's `focus-visible:ring-2 focus-visible:ring-ring`
            // would both reach the DOM and stylesheet order would pick the ring.
            // Through `cn()` tailwind-merge resolves the pair.
            className={cn(
              badgeVariants({ variant: 'muted', size: 'xs' }),
              PILL_CLASS,
              'transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
            )}
          >
            {updateStudy.isPending ? (
              <Spinner aria-hidden className="size-3" />
            ) : null}
            {label}
          </button>
        </PopoverTrigger>
      </SimpleTooltip>
      <PopoverContent align="start" sideOffset={6} className="w-72 p-1">
        <RowLabel className="px-2 py-1.5">
          {t('simopsShared.studyKind.heading')}
        </RowLabel>
        <div className="flex flex-col">
          {OPTIONS.map((opt) => {
            const selected = opt.kind === current;
            const disabled = opt.disabled || updateStudy.isPending;
            return (
              <button
                key={opt.kind}
                type="button"
                disabled={disabled}
                onClick={() => handlePick(opt.kind)}
                className={cn(
                  'flex w-full items-start gap-2 rounded-sm px-2 py-2 text-left transition-colors',
                  !disabled && 'cursor-pointer hover:bg-muted',
                  disabled && 'cursor-not-allowed opacity-60',
                  selected && 'bg-muted/70',
                )}
              >
                <span className="mt-0.5 flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center">
                  {selected ? (
                    <Check className="h-3 w-3 text-foreground" />
                  ) : null}
                </span>
                <div className="flex flex-1 flex-col gap-0.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-foreground">
                      {t(`simopsShared.studyKind.options.${opt.kind}.label`)}
                    </span>
                    {opt.disabled && (
                      <Badge
                        variant="muted"
                        size="xs"
                        // `size="xs"` is the nearest rung (`text-3xs`); this
                        // chip sits a step below it, and `bg-card` keeps it
                        // legible over the row's `hover:bg-muted`.
                        className="border-border bg-card px-1.5 py-px font-mono text-4xs tracking-caps uppercase"
                      >
                        {t('simopsShared.studyKind.comingSoon')}
                      </Badge>
                    )}
                  </div>
                  <span className="text-2xs leading-snug text-muted-foreground">
                    {t(`simopsShared.studyKind.options.${opt.kind}.subtitle`)}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
        {updateStudy.isError && (
          <div className="border-t border-border px-2 py-1.5 text-2xs text-destructive">
            {t('simopsShared.studyKind.changeError')}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
